import { Component, OnDestroy, OnInit } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { of, Subject } from 'rxjs';
import { catchError, tap, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { Summary } from './summary/summary.interface';
import { Router } from '@angular/router';
import {
    CardConnectManualEntryData,
    CardConnectProfileAccountData,
} from './card-connect.interface';
import {
    PurchaseGiftCardViaCardConnectManual,
    PurchaseGiftCardViaCardConnectProfile,
} from './purchase.interface';
import { CheckoutFormService } from './checkout-form-service/checkout-form.service';
import { CheckoutState, SuccessfulPurchaseState } from '../checkout.state';
import { SuccessfulPurchase } from '../successful-purchase/successful-purchase.interface';
import { StripeElementsService } from 'src/app/shared/stripe-elements/stripe-elements.service';
import {
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
} from '@angular/material/dialog';
import { PaymentProcessingComponent } from './payment-processing/payment-processing.component';
import { LoginService } from 'src/app/shared/login/login-service/login.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { StripeSurchargeSettings } from './stripe-surcharge-settings.interface';
import {
    PurchaseGiftCardViaStripeInput,
    PurchaseGiftCardViaStripeResponse,
} from './stripe.interface';
import { CreditCardProcessingService } from 'src/app/shared/credit-card-processing/credit-card-processing.service';
import { CreditCardProcessingMethod } from 'src/app/shared/credit-card-processing-method.enum';

const query = gql`
    query Query($customerID: Int!, $token: String) {
        giftCardSettings(customerID: $customerID) {
            policy
        }
        olsCheckoutState @client {
            itemImagePath
            recipientName
            recipientEmail
            itemTotal
            itemNote
            senderEmail
            senderName
            giftCardDesignID
            expirationDays
        }
    }
`;

const subscription = gql`
    subscription Subscription(
        $recipientEmail: String
        $transactionGuid: String
    ) {
        giftCardPurchased(
            recipientEmail: $recipientEmail
            transactionGuid: $transactionGuid
        ) {
            giftCardAmount
            giftCardID
            recipientEmail
            transactionID
            printableHtml
            transactionGuid
            error
        }
    }
`;

const purchaseManualMutation = gql`
    mutation Mutation(
        $customerID: ID!
        $input: PurchaseGiftCardViaCardConnectManualInput!
    ) {
        initPurchaseGiftCardViaCardConnectManual(
            customerID: $customerID
            input: $input
        ) {
            status
        }
    }
`;

const purchaseProfileMutation = gql`
    mutation Mutation(
        $customerID: ID!
        $input: PurchaseGiftCardViaCardConnectProfileInput!
    ) {
        initPurchaseGiftCardViaCardConnectProfile(
            customerID: $customerID
            input: $input
        ) {
            status
        }
    }
`;

const stripeSurchargeQuery = gql`
    query GetStripeSurchargeSettings($customerID: Int!) {
        stripeSurchargeSettings(customerID: $customerID) {
            surchargeEnabled
            surchargeRate
        }
    }
`;

const stripePaymentMutation = gql`
    mutation PurchaseGiftCardViaStripe(
        $input: PurchaseGiftCardViaStripeInput!
    ) {
        purchaseGiftCardViaStripe(input: $input) {
            giftCardAmount
            giftCardID
            printableHtml
            recipientEmail
            transactionID
            transactionGuid
            error
        }
    }
`;
@Component({
    selector: 'ols-purchase-and-summary',
    templateUrl: './purchase-and-summary.component.html',
    styleUrls: ['./purchase-and-summary.component.scss'],
})
export class PurchaseAndSummaryComponent implements OnInit, OnDestroy {
    isLoading = true;
    summary: Summary;
    purchaseAgreement: string;
    stripeSurchargeSettings: StripeSurchargeSettings | null = null;

    private _isCheckingOut = false;
    get isCheckingOut(): boolean {
        return this._isCheckingOut;
    }
    set isCheckingOut(newValue: boolean) {
        this._isCheckingOut = newValue;
        if (newValue) {
            this.openProcessingDialog();
        } else {
            this.closeProcessingDialog();
        }
    }
    private dialogRef: MatDialogRef<any>;
    private loggedInClientID: number;
    private loggedInUserAccountGuid: string;
    private ngUnsubscribe: Subject<any> = new Subject();

    constructor(
        private apollo: Apollo,
        private notificationService: NotificationService,
        private formService: CheckoutFormService,
        private router: Router,
        private dialog: MatDialog,
        private loginService: LoginService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private creditCardProcessingService: CreditCardProcessingService,
        private stripeElementsService: StripeElementsService
    ) {}

    ngOnInit(): void {
        this.apollo
            .watchQuery({
                query,
            })
            .valueChanges.pipe(
                takeUntil(this.ngUnsubscribe),
                tap((response: any) => {
                    if (response?.data?.olsCheckoutState) {
                        this.summary = response.data.olsCheckoutState;
                        this.purchaseAgreement =
                            response.data.giftCardSettings.policy;
                        this.isLoading = false;
                        this.loadStripeSurchargeSettings();
                    } else if (response && response.errors) {
                        this.notificationService.showFailure(response.errors);
                    }
                }),
                catchError((err) => {
                    this.notificationService.showFailure(err);
                    return of(null);
                })
            )
            .subscribe();

        this.formService
            .formSubmitted$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((_) => {
                this.initiatePurchase();
            });

        this.formService
            .stripeFormSubmitted$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.processStripeGiftCardPurchase();
            });

        this.loginService.loginState$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((value) => {
                if (value) {
                    this.loggedInClientID = value.clientID;
                    this.loggedInUserAccountGuid = value.userAccountGuid;
                }
            });
    }

    submitForm(): void {
        const processingMethod = this.creditCardProcessingService.getCreditCardProcessingMethod();
        if (processingMethod === CreditCardProcessingMethod.Stripe) {
            this.formService.submitStripeForm();
        } else {
            this.formService.submitForm();
        }
    }

    goBack(): void {
        this.router.navigate(['/gift']);
    }

    private loadStripeSurchargeSettings(): void {
        const customerID = parseInt(
            localStorage.getItem('Customer_ID') || '0',
            10
        );
        this.apollo
            .watchQuery({
                query: stripeSurchargeQuery,
                variables: {
                    customerID: customerID,
                },
            })
            .valueChanges.pipe(
                takeUntil(this.ngUnsubscribe),
                tap((response: any) => {
                    if (response?.data?.stripeSurchargeSettings) {
                        this.stripeSurchargeSettings =
                            response.data.stripeSurchargeSettings;
                    } else if (response?.errors) {
                        this.notificationService.showFailure(response.errors);
                    }
                }),
                catchError((err) => {
                    this.notificationService.showFailure(err);
                    return of(null);
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    openProcessingDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.hasBackdrop = false;

        this.dialogRef = this.dialog.open(
            PaymentProcessingComponent,
            dialogConfig
        );
    }

    closeProcessingDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }

        this.dialogRef = undefined;
    }

    private generateUniqueGuid(): string {
        let dt = new Date().getTime();
        const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            function (c) {
                // eslint-disable-next-line no-bitwise
                const r = (dt + Math.random() * 16) % 16 | 0;
                dt = Math.floor(dt / 16);
                // eslint-disable-next-line no-bitwise
                return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            }
        );
        return uuid;
    }

    private async initiatePurchase(): Promise<void> {
        if (this.formService.formIsValid()) {
            this.isCheckingOut = true;
            this.initiateCardConnectPurchase();
        }
    }

    private initiateCardConnectPurchase(): void {
        this.recaptchaV3Service
            .execute('checkout')
            .subscribe((token: string) => {
                const transactionGuid = this.generateUniqueGuid();
                const paymentData = this.formService.getData();
                const linkToUserAccount = this.formService.form.get(
                    'linkToUserAccount'
                ).value;
                const purchaseInfo:
                    | PurchaseGiftCardViaCardConnectManual
                    | PurchaseGiftCardViaCardConnectProfile = {
                    balance: parseFloat(this.summary.itemTotal.toString()),
                    expirationDays: this.summary.expirationDays,
                    giftCardDesignID: this.summary.giftCardDesignID,
                    senderName: this.summary.senderName,
                    senderEmail: this.summary.senderEmail,
                    recipientName: this.summary.recipientName,
                    recipientEmail: this.summary.recipientEmail,
                    itemNote: encodeURI(this.summary.itemNote),
                    clientID: linkToUserAccount ? this.loggedInClientID : 0,
                    userAccountGuid: linkToUserAccount
                        ? this.loggedInUserAccountGuid
                        : '',
                    creditCardToken: (paymentData as CardConnectManualEntryData)
                        .creditCardToken,
                    creditCardExpirationMonth: (paymentData as CardConnectManualEntryData)
                        .creditCardExpirationMonth,
                    creditCardExpirationYear: (paymentData as CardConnectManualEntryData)
                        .creditCardExpirationYear,
                    creditCardZipCode: (paymentData as CardConnectManualEntryData)
                        .creditCardZipCode,
                    creditCardCvv: (paymentData as CardConnectManualEntryData)
                        .creditCardCvv,
                    creditCardClientName: (paymentData as CardConnectManualEntryData)
                        .creditCardClientName,
                    saveCardOnFile: (paymentData as CardConnectManualEntryData)
                        .saveCardOnFile,
                    profileAccountID: (paymentData as CardConnectProfileAccountData)
                        .id,
                    transactionGuid: transactionGuid,
                    token: token,
                };

                this.apollo
                    .subscribe({
                        query: subscription,
                        variables: {
                            recipientEmail: purchaseInfo.recipientEmail,
                            transactionGuid: transactionGuid,
                        },
                    })
                    .pipe(takeUntil(this.ngUnsubscribe))
                    .subscribe((response: any) => {
                        if (response?.data?.giftCardPurchased?.giftCardID) {
                            CheckoutState(null);
                            SuccessfulPurchaseState(
                                <SuccessfulPurchase>(
                                    response.data.giftCardPurchased
                                )
                            );
                            this.isCheckingOut = false;
                            this.router.navigate([
                                `/checkout/successful-purchase`,
                            ]);
                        } else {
                            this.processPurchaseError(response);
                        }
                    });

                this.apollo
                    .mutate({
                        mutation:
                            (purchaseInfo as PurchaseGiftCardViaCardConnectProfile)
                                .profileAccountID?.length > 0
                                ? purchaseProfileMutation
                                : purchaseManualMutation,
                        variables: {
                            input: purchaseInfo,
                        },
                    })
                    .pipe(
                        takeUntil(this.ngUnsubscribe),
                        catchError((err) => {
                            this.handleCheckoutFailure(err);
                            return of(null);
                        })
                    )
                    .subscribe((response) => {
                        if (response.errors) {
                            console.error(response.errors);
                            this.handleCheckoutFailure();
                        }
                    });
            });
    }

    handleCheckoutFailure(err?: any): void {
        this.notificationService.showFailure(err);
        this.isCheckingOut = false;
    }

    private async processStripeGiftCardPurchase(): Promise<void> {
        try {
            this.isCheckingOut = true;

            let paymentMethodId: string;
            if (this.formService.isUsingSavedCard()) {
                const stripePaymentData = this.formService.getStripePaymentData();
                paymentMethodId = stripePaymentData.paymentMethodId;
            } else {
                const stripeElements = this.formService.getStripePaymentElements();

                const { error: submitError } = await stripeElements.submit();
                if (submitError) {
                    this.notificationService.showFailure(
                        `Payment validation failed: ${submitError.message}`
                    );
                    this.isCheckingOut = false;
                    return;
                }

                const {
                    error: paymentMethodError,
                    paymentMethod,
                } = await this.stripeElementsService.createPaymentMethod(
                    stripeElements
                );

                if (paymentMethodError || !paymentMethod) {
                    this.notificationService.showFailure(
                        `Payment method creation failed: ${
                            paymentMethodError?.message || 'Unknown error'
                        }`
                    );
                    this.isCheckingOut = false;
                    return;
                }

                paymentMethodId = paymentMethod.id;
            }

            this.recaptchaV3Service
                .execute('checkout')
                .subscribe(async (token: string) => {
                    try {
                        const transactionGuid = this.generateUniqueGuid();
                        const linkToUserAccount =
                            this.formService.form?.get('linkToUserAccount')
                                ?.value || false;
                        const saveCard =
                            this.formService.form?.get('saveCard')?.value ||
                            false;
                        const customerID =
                            localStorage.getItem('Customer_ID') || '0';

                        const purchaseInfo: PurchaseGiftCardViaStripeInput = {
                            customerID: customerID,
                            giftCardDesignID: this.summary.giftCardDesignID.toString(),
                            senderName: this.summary.senderName,
                            senderEmail: this.summary.senderEmail,
                            recipientEmail: this.summary.recipientEmail,
                            recipientName: this.summary.recipientName,
                            paymentMethodID: paymentMethodId,
                            userAccountGuid: linkToUserAccount
                                ? this.loggedInUserAccountGuid
                                : '',
                            amount: Math.round(
                                parseFloat(this.summary.itemTotal.toString()) *
                                    100
                            ),
                            currency: 'usd',
                            transactionGuid: transactionGuid,
                            token: token,
                            clientID: linkToUserAccount
                                ? this.loggedInClientID.toString()
                                : '0',
                            saveCard: saveCard,
                            itemNote: encodeURI(this.summary.itemNote),
                            expirationDays: this.summary.expirationDays,
                        };

                        this.apollo
                            .mutate({
                                mutation: stripePaymentMutation,
                                variables: {
                                    input: purchaseInfo,
                                },
                            })
                            .pipe(
                                takeUntil(this.ngUnsubscribe),
                                catchError((err) => {
                                    this.handleCheckoutFailure(err);
                                    return of(null);
                                })
                            )
                            .subscribe((response: any) => {
                                if (response?.data?.purchaseGiftCardViaStripe) {
                                    const result: PurchaseGiftCardViaStripeResponse =
                                        response.data.purchaseGiftCardViaStripe;
                                    if (result.giftCardID) {
                                        CheckoutState(null);
                                        SuccessfulPurchaseState(<
                                            SuccessfulPurchase
                                        >{
                                            transactionID: parseInt(
                                                result.transactionID,
                                                10
                                            ),
                                            recipientEmail:
                                                result.recipientEmail,
                                            printableHtml: result.printableHtml,
                                            giftCardAmount:
                                                result.giftCardAmount,
                                            giftCardID: result.giftCardID,
                                        });
                                        this.isCheckingOut = false;
                                        this.router.navigate([
                                            '/checkout/successful-purchase',
                                        ]);
                                    } else if (result.error) {
                                        this.processPurchaseError({
                                            data: { giftCardPurchased: result },
                                        });
                                    }
                                } else if (response?.errors) {
                                    console.error(
                                        'GraphQL errors:',
                                        response.errors
                                    );
                                    this.handleCheckoutFailure(
                                        'Payment processing failed'
                                    );
                                }
                            });
                    } catch (error) {
                        console.error(
                            'Error in Stripe purchase processing:',
                            error
                        );
                        this.handleCheckoutFailure(
                            error.message ||
                                'An unexpected error occurred during payment processing.'
                        );
                    }
                });
        } catch (error) {
            console.error('Error in processStripeGiftCardPurchase:', error);
            this.handleCheckoutFailure(
                error.message ||
                    'An unexpected error occurred during payment processing.'
            );
        }
    }

    private processPurchaseError(response: any): void {
        const error = response?.data?.giftCardPurchased?.error;
        let errorMessage: string;
        try {
            const parsedError = JSON.parse(error);

            errorMessage = parsedError.errorMessage;
        } catch (parsingError) {
            if (typeof error === 'string') {
                errorMessage = error;
            } else {
                try {
                    console.error(error);
                    console.error(parsingError);
                } catch {
                    /* Don't break everything because console logging failed */
                }
                errorMessage =
                    'Something went wrong, please contact us for further assistance.';
            }
        }

        this.notificationService.showFailure(errorMessage);
        this.isCheckingOut = false;
    }
}
